import { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../providers/firebase/firebase';
import { reportIfLegalRequired } from '../legal/legalRequired';

interface CloudFunctionResponse<TRaw> {
    rows: TRaw[];
    truncated: boolean;
}

/**
 * Generic hook for calling Firebase Cloud Functions that return { rows, truncated }.
 *
 * @param functionName  Cloud Function name (e.g. 'loadAccountTrades')
 * @param params        Full params object to send, or null to skip the call
 * @param transform     Maps raw rows into the desired result type
 */
export function useCloudFunction<TRaw, TResult>(
    functionName: string,
    params: Record<string, unknown> | null,
    transform: (rows: TRaw[]) => TResult[],
): { data: TResult[]; loading: boolean; error: Error | null } {
    const [data, setData] = useState<TResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const callIdRef = useRef(0);
    const transformRef = useRef(transform);
    transformRef.current = transform;

    const paramsJson = JSON.stringify(params);

    useEffect(() => {
        if (!params) {
            setData([]);
            setLoading(false);
            return;
        }

        const callId = ++callIdRef.current;
        setLoading(true);
        setError(null);

        const callable = httpsCallable<
            Record<string, unknown>,
            CloudFunctionResponse<TRaw>
        >(functions, functionName);

        callable(params)
            .then((result) => {
                if (callId !== callIdRef.current) return;
                setData(transformRef.current(result.data.rows));
            })
            .catch((err) => {
                if (callId !== callIdRef.current) return;
                console.error(`${functionName} failed:`, err);
                reportIfLegalRequired(err);
                setError(err);
                setData([]);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });
        // paramsJson is a stable serialization of params — re-fetches when values change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [functionName, paramsJson]);

    return { data, loading, error };
}
